import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { InGameCurrency, Rarity } from '#src/__generated__/globalTypes.ts';
import fiftyFiftyCraftClueUrl from '#src/assets/crafting-clues/best-five.webp';
import bestStarRankCraftClueUrl from '#src/assets/crafting-clues/best-star-rank.webp';
import competitionCraftClueUrl from '#src/assets/crafting-clues/competition.webp';
import divisionClueUrl from '#src/assets/crafting-clues/division.webp';
import globalCupCraftClueUrl from '#src/assets/crafting-clues/global-cup.webp';
import countryCraftClueUrl from '#src/assets/crafting-clues/nationality.webp';
import positionClueUrl from '#src/assets/crafting-clues/position.webp';
import { Cash } from '#src/atoms/icons/Cash.tsx';
import { GemIcon } from '#src/atoms/icons/GemIcon.tsx';
import { Packs } from '#src/atoms/icons/Packs.tsx';
import { Shards } from '#src/atoms/icons/Shards.tsx';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';
import { isType } from '#src/lib/gql/index.ts';
import {
  getShardImageUrl,
  isXPCurrency,
  stripRarityFromCraftingClue,
} from '#src/lib/rewards.tsx';
import { inGameCurrencyToRarity } from '#src/lib/scarcity.ts';
import type { Unit } from '#src/lib/style.ts';
import { toSnakeCase } from '#src/lib/toSnakeCase.ts';
import {
  isGoldenWheelTicketCurrency,
  isWheelTicketCurrency,
} from '#src/lib/wheelCurrencies.ts';

import type { RewardConfigImage_anyRewardConfigInterface } from './__generated__/index.graphql.ts';

const REWARDS_BASE_URL = `${FRONTEND_ASSET_HOST}/rewards/`;

const LARGEST_FLAT_ICON_SIZE = 2;

const Img = styled.img`
  object-fit: contain;
`;

const getFlatIcon = (
  anyRewardConfig: RewardConfigImage_anyRewardConfigInterface,
  size: Unit,
  inactive: boolean,
  alt: string
) => {
  const color = inactive ? 'var(--c-nd-400)' : 'var(--c-white)';
  const label = alt ? ({ role: 'img', 'aria-label': alt } as const) : undefined;

  if (isType(anyRewardConfig, 'MonetaryRewardConfig')) {
    return <Cash size={size} color={color} {...label} />;
  }
  if (isType(anyRewardConfig, 'CardShardRewardConfig')) {
    return <Shards size={size} color={color} {...label} />;
  }
  if (isType(anyRewardConfig, 'CardPacksRewardConfig')) {
    return <Packs size={size} color={color} {...label} />;
  }
  if (
    isType(anyRewardConfig, 'InGameCurrencyRewardConfig') &&
    stripRarityFromCraftingClue(anyRewardConfig.currency) ===
      InGameCurrency.COMMON_GEM
  ) {
    return (
      <GemIcon
        size={size}
        className={inactive ? 'opacity-50' : undefined}
        {...label}
      />
    );
  }

  return null;
};

const getRewardImageUrl = (
  anyRewardConfig: RewardConfigImage_anyRewardConfigInterface,
  rarity: Rarity | 'mix' | undefined
): string => {
  if (isType(anyRewardConfig, 'ShopItemRewardConfig')) {
    return anyRewardConfig.shopItem.pictureUrl;
  }
  if (
    isType(anyRewardConfig, 'ExperienceRewardConfig') &&
    anyRewardConfig.pictureUrl
  ) {
    return anyRewardConfig.pictureUrl;
  }
  if (
    isType(anyRewardConfig, 'CardPacksRewardConfig') &&
    anyRewardConfig.cardPack?.pictureUrl
  ) {
    return anyRewardConfig.cardPack.pictureUrl;
  }
  if (
    isType(anyRewardConfig, 'CardRewardConfig') &&
    anyRewardConfig.pictureUrl
  ) {
    return anyRewardConfig.pictureUrl;
  }

  let url: string = '';
  switch (anyRewardConfig.__typename) {
    case 'CardPacksRewardConfig':
      url = `card-common`;
      break;
    case 'CardRewardConfig':
      url = `card-${toSnakeCase(rarity ?? anyRewardConfig.configRarity.toLowerCase())}`;
      break;
    case 'CardShardRewardConfig':
      return getShardImageUrl(
        anyRewardConfig.cardSet,
        toSnakeCase(rarity ?? anyRewardConfig.configRarity.toLowerCase())
      );
    case 'ConversionCreditRewardConfig':
      url = anyRewardConfig.percentageDiscount === 1 ? `credit-100` : `credit`;
      break;
    case 'MonetaryRewardConfig':
      url = `cash`;
      break;
    case 'ExperienceRewardConfig':
      url = `experience`;
      break;
    case 'DeliverableItemRewardConfig':
      if (isType(anyRewardConfig.deliverableItem, 'JerseyDeliverableItem')) {
        url = `jersey`;
      }
      if (isType(anyRewardConfig.deliverableItem, 'WearableDeliverableItem')) {
        url = `hat`;
      }
      if (
        isType(anyRewardConfig.deliverableItem, 'ExperienceDeliverableItem')
      ) {
        url = `experience`;
      }
      if (isType(anyRewardConfig.deliverableItem, 'TicketsDeliverableItem')) {
        url = `ticket`;
      }
      break;
    case 'ProbabilisticBundleRewardConfig':
      url = `box-${toSnakeCase(anyRewardConfig.probabilisticBundleConfig.rarity.toLowerCase())}`;
      break;
    case 'InGameCurrencyRewardConfig': {
      const currency = stripRarityFromCraftingClue(anyRewardConfig.currency);
      if (isXPCurrency(currency)) {
        url = `xp-${toSnakeCase(((rarity === 'mix' && rarity) || inGameCurrencyToRarity[currency] || Rarity.common).toLowerCase())}`;
      }
      if (currency === InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE) {
        return fiftyFiftyCraftClueUrl;
      }
      if (currency === InGameCurrency.COMPETITION_CRAFT_CLUE) {
        return competitionCraftClueUrl;
      }
      if (currency === InGameCurrency.COUNTRY_CRAFT_CLUE) {
        return countryCraftClueUrl;
      }
      if (currency === InGameCurrency.POSITION_CRAFT_CLUE) {
        return positionClueUrl;
      }
      if (currency === InGameCurrency.DIVISION_CRAFT_CLUE) {
        return divisionClueUrl;
      }
      if (currency === InGameCurrency.BEST_STAR_RANK_CRAFT_CLUE) {
        return bestStarRankCraftClueUrl;
      }
      if (currency === InGameCurrency.SPECIAL_EVENT_CRAFT_CLUE) {
        return globalCupCraftClueUrl;
      }
      if (currency === InGameCurrency.COMMON_GEM) {
        url = `common-gem-3d`;
      }
      if (isWheelTicketCurrency(currency)) {
        url = `wheel-ticket-${toSnakeCase(inGameCurrencyToRarity[currency] ?? Rarity.common).toLowerCase()}`;
      }
      if (isGoldenWheelTicketCurrency(currency)) {
        url = 'wheel-ticket-limited';
      }
      if (currency === InGameCurrency.SET_RAFFLE_TICKET) {
        url = 'set-raffle-ticket';
      }
      break;
    }
  }

  return `${REWARDS_BASE_URL}${url}.png`;
};

type Props = {
  anyRewardConfig: RewardConfigImage_anyRewardConfigInterface;
  rarity?: Rarity | 'mix';
  size?: Unit;
  alt?: string;
  inactive?: boolean;
  flat?: boolean;
};

export const RewardConfigImage = ({
  anyRewardConfig,
  rarity,
  size = 3,
  alt = '',
  inactive = false,
  flat = size <= LARGEST_FLAT_ICON_SIZE,
}: Props) => {
  const flatIcon = flat && getFlatIcon(anyRewardConfig, size, inactive, alt);
  if (flatIcon) {
    return flatIcon;
  }

  const url = getRewardImageUrl(anyRewardConfig, rarity);
  if (!url) {
    return null;
  }

  return (
    <Img
      src={url}
      alt={alt}
      width={size * 8}
      height={size * 8}
      className={inactive ? 'opacity-30' : undefined}
    />
  );
};

RewardConfigImage.fragments = {
  anyRewardConfigInterface: gql`
    fragment RewardConfigImage_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on ConversionCreditRewardConfig {
        id
        percentageDiscount
      }
      ... on CardRewardConfig {
        id
        configRarity
        pictureUrl
      }
      ... on CardShardRewardConfig {
        id
        configRarity
        cardSet {
          slug
          branding {
            id
            setCurrency: assetUrl(slot: BRANDING_SET_CURRENCY_MOBILE)
          }
        }
      }
      ... on ProbabilisticBundleRewardConfig {
        id
        probabilisticBundleConfig {
          id
          rarity
        }
      }
      ... on InGameCurrencyRewardConfig {
        id
        currency
      }
      ... on ShopItemRewardConfig {
        id
        shopItem {
          id
          pictureUrl
        }
      }
      ... on DeliverableItemRewardConfig {
        id
        deliverableItem {
          slug
        }
      }
      ... on ExperienceRewardConfig {
        id
        pictureUrl
      }
      ... on CardPacksRewardConfig {
        id
        cardPack {
          slug
          pictureUrl
        }
      }
    }
  ` as TypedDocumentNode<RewardConfigImage_anyRewardConfigInterface>,
};
